import Image from "components/Elements/Image/Image.tsx";
import Icon from "components/Elements/Icon/Icon.tsx";
import Button, { ButtonA } from "components/Elements/Button/Button.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import ImageAnimatedWithContainer from "./ImageAnimatedWithContainer";
import SecuritySection from "./SecuritySection";
import ButtonsContainer from "./ButtonsContainer";
import MainBanner from "./components/MainBanner";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useUser } from "contexts/userContext/userContext";
import { useEffect } from "react";

import "./business.scss";

const Business = () => {
  const { getTextByKey, getHtmlByKey, getDirection } = useContent();
  const { mobileCondition, desktopCondition } = useMediaQueries();
  const { getIsIsrael } = useUser();

  const desktopRowClass = !mobileCondition ? "row" : "";

  const animateStart = getDirection() === "rtl" ? "right" : "left";
  const animateEnd = getDirection() === "rtl" ? "left" : "right";

  const handleScrollDown = (
    e: Event & {
      target: { scrollIntoView: (arg0: { behavior: string }) => void };
    }
  ) => {
    e.target.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    document.title = getTextByKey("business-page-title") + " — JumboMail";
  }, [getTextByKey]);

  return (
    <div
      className="business-container"
      aria-label={getTextByKey("business-page-aria-label")}
    >
      <div
        className="content-section main-banner-section"
        aria-labelledby="business-page-title"
      >
        <div className={`content-text row no-margin-bottom sub-title`}>
          <Icon name="briefcase" lightgray aria-hidden="true" />
          {getTextByKey("business-page-subtitle")}
        </div>
        <h1 className="main-title" id="business-page-title">
          {getTextByKey("business-page-title")}
        </h1>
        {!desktopCondition ? (
          <Image
            name="business/business-banner"
            ext="gif"
            className="graphic-full-width blendmode main-banner"
            alt={getTextByKey("business-banner-alt")}
          />
        ) : (
          <MainBanner>
            <Image
              name="business/business-banner"
              ext="gif"
              className="graphic-full-width blendmode main-banner"
              alt={getTextByKey("business-banner-alt")}
            />
          </MainBanner>
        )}
        <Button buttonClass="margin-top-20" onClick={handleScrollDown}>
          {getTextByKey("get-started-text")}
        </Button>
        <Image
          name="business/banner-bg"
          ext="webp"
          className="background-image"
          alt=""
        />
      </div>
      <div className={`content-section ${!mobileCondition ? "row" : ""}`}>
        <div className="col-5">
          <h2 className="section-title" id="business-page-section-1-title">
            {getTextByKey("business-page-section-1-title")}
          </h2>
          <div className="content-text">
            <p>{getHtmlByKey("business-page-section-1-info")}</p>
          </div>
        </div>
        <ImageAnimatedWithContainer
          imagesNames={[`business/sub-users${mobileCondition ? "-mob" : ""}`]}
          ext="png"
          className="graphic-full-width"
          animationDirection={animateEnd}
          containerClassName="col-7"
        />
      </div>
      <div className={`content-section ${desktopRowClass} dark `}>
        {!mobileCondition && (
          <>
            <SecuritySection />
            <div className="col-1"></div>
          </>
        )}
        <div className="col-5">
          <h2 className="section-title light">
            {getTextByKey("business-page-section-2-title")}
          </h2>
          <div className="content-text light">
            <p>{getHtmlByKey("business-page-section-2-info")}</p>
          </div>
          <div className="buttons-container security">
            <ButtonA
              transparent
              target="_blank"
              page="features/advanced-security-options"
              img={null}
              title={getTextByKey("learn-more")}
              buttonClass=""
            >
              {getTextByKey("learn-more")}
            </ButtonA>
            <ButtonA
              transparent
              target="_blank"
              href="https://trust.jumbomail.me/"
              img={null}
              title={getTextByKey("trust-center")}
              buttonClass=""
              page={null}
            >
              {getTextByKey("trust-center")}
            </ButtonA>
          </div>
        </div>
        {mobileCondition && <SecuritySection />}
      </div>
      <div className={`content-section ${desktopRowClass} padding-top-100`}>
        <Image name="business/form_bg" ext="png" className="background-image" />
        {mobileCondition ? (
          <>
            <div className="col-12">
              <h2 className="section-title">
                {getTextByKey("business-page-section-3-title")}
              </h2>
              <div className="content-text darker">
                {getHtmlByKey("business-page-section-3-info")}
              </div>
            </div>
            <div className="col-12">
              <ImageAnimatedWithContainer
                containerClassName="col-4"
                imagesNames={[
                  "business/form-mob",
                  "business/manage-mob",
                  "business/form-1",
                  "business/manage-1",
                ]}
                ext="png"
                className="graphic-full-width"
              />
              <div className="buttons-container">
                <ButtonA
                  transparent
                  buttonClass="auto-width"
                  target="_blank"
                  page="jumbo-forms"
                  img={null}
                  title={getTextByKey("learn-more")}
                >
                  {getTextByKey("learn-more")}
                </ButtonA>
              </div>
            </div>
          </>
        ) : (
          <>
            <ImageAnimatedWithContainer
              containerClassName="col-4 margin-top-100"
              imagesNames={["business/form", "business/form-1"]}
              ext="png"
              className="graphic-full-width"
              animationDirection={animateStart}
            />
            <div className="col-4 aling-end margin-20">
              <h2 className="section-title">
                {getTextByKey("business-page-section-3-title")}
              </h2>
              <div className="content-text darker">
                {getHtmlByKey("business-page-section-3-info")}
                <ButtonA
                  transparent
                  buttonClass="auto-width"
                  target="_blank"
                  page="jumbo-forms"
                  img={null}
                  title={getTextByKey("learn-more")}
                >
                  {getTextByKey("learn-more")}
                </ButtonA>
              </div>
            </div>
            <ImageAnimatedWithContainer
              containerClassName="col-4 custom-column-section-3"
              imagesNames={["business/manage", "business/manage-1"]}
              ext="png"
              className="graphic-full-width"
              animationDirection={animateEnd}
            />
          </>
        )}
      </div>
      <div className="content-section">
        <h2 className="section-title button-section">
          {getTextByKey("call-to-action-splitter-1")}
        </h2>
        <ButtonsContainer />
      </div>
      {mobileCondition ? (
        <div className="content-section dark">
          <div className="col-12">
            <h2 className="section-title light">
              {getTextByKey("business-page-section-4-title")}
            </h2>
            <div className="content-text light">
              <p>{getHtmlByKey("business-page-section-4-info")}</p>
            </div>
          </div>
          <ImageAnimatedWithContainer
            containerClassName="col-12"
            imagesNames={[
              "business/branding",
              "business/branding-1",
              "business/branding-2",
              "business/branding-3",
            ]}
            ext="png"
            className="graphic-full-width"
          />
          <div className="col-12">
            <h2 className="section-title light">
              {getTextByKey("business-page-section-5-title")}
            </h2>
            <div className="content-text light">
              <p>{getHtmlByKey("business-page-section-5-info")}</p>
            </div>
          </div>
          <div className="col-12">
            <Image
              name="business/storage"
              ext="png"
              className="graphic-full-width with-margin"
            />
          </div>
          <div className="col-12">
            <h2 className="section-title light">
              {getTextByKey("business-page-section-6-title")}
            </h2>
            <div className="content-text light">
              <p>{getHtmlByKey("business-page-section-6-info")}</p>
            </div>
          </div>
          <ImageAnimatedWithContainer
            containerClassName="col-12 margin-top-50"
            imagesNames={[
              "business/dashboard-mob",
              "business/dashboard-1-mob",
              "business/dashboard-2-mob",
              "business/dashboard-3",
            ]}
            ext="png"
            className="graphic-full-width"
          />
        </div>
      ) : (
        <div className="content-section dark">
          <Image
            name="business/abstract-circle-lines"
            ext="png"
            className="background-effect no-gradient"
            style={{
              left: "10vw",
              top: 0,
              width: "22vw",
              height: "22vw",
            }}
          />
          <div
            className="background-effect"
            style={{
              left: "10vw",
              top: 0,
            }}
          />
          <div
            className="background-effect"
            style={{
              top: "40%",
              width: "20vw",
              height: "20vw",
              left: "30vw",
              opacity: 0.2,
            }}
          />
          <div className={desktopRowClass}>
            <div className="col-5">
              <h2 className="section-title light">
                {getTextByKey("business-page-section-4-title")}
              </h2>
              <div className="content-text light">
                <p>{getHtmlByKey("business-page-section-4-info")}</p>
              </div>
            </div>
            <div className="col-1" />
            <ImageAnimatedWithContainer
              containerClassName="col-6"
              imagesNames={[
                "business/branding",
                "business/branding-1",
                "business/branding-2",
                "business/branding-3",
              ]}
              ext="png"
              className="graphic-full-width"
            />
          </div>
          <div className={desktopRowClass}>
            <ImageAnimatedWithContainer
              containerClassName="col-4"
              animationDirection={animateStart}
              imagesNames={["business/storage"]}
              ext="png"
              className="graphic-full-width"
            />
            <div className="col-2"></div>
            <div className="col-6">
              <h2 className="section-title light">
                {getTextByKey("business-page-section-5-title")}
              </h2>
              <div className="content-text light">
                <p>{getHtmlByKey("business-page-section-5-info")}</p>
              </div>
            </div>
          </div>
          <div className={desktopRowClass}>
            <div className="col-5">
              <h2 className="section-title light">
                {getTextByKey("business-page-section-6-title")}
              </h2>
              <div className="content-text light">
                <p>{getHtmlByKey("business-page-section-6-info")}</p>
              </div>
            </div>
            <div className="col-2" />
            <ImageAnimatedWithContainer
              containerClassName="col-5 custom-column-section-6"
              imagesNames={[
                "business/dashboard",
                "business/dashboard-1",
                "business/dashboard-2",
                "business/dashboard-3",
              ]}
              ext="png"
              className="graphic-full-width"
            />
          </div>
        </div>
      )}
      {getIsIsrael() ? (
        <>
          <div className="content-section">
            <h2 className="section-title button-section">
              {getTextByKey("call-to-action-splitter-2")}
            </h2>
            <div className="buttons-container">
              <ButtonA
                buttonClass="no-margin-bottom"
                page="plans"
                target="_blank"
                img={null}
                title={getTextByKey("get-started-text")}
              >
                {getTextByKey("get-started-text")}
              </ButtonA>
            </div>
          </div>
          <div
            className={`content-section no-padding-inline ${desktopRowClass} ${
              mobileCondition ? "custom-style-section-8" : "no-padding-bottom"
            }`}
          >
            <div className="background-image colored-background" />
            {!mobileCondition && (
              <>
                <ImageAnimatedWithContainer
                  containerClassName="col-4"
                  imagesNames={["business/jpay"]}
                  ext="png"
                  className="graphic-full-width"
                  animationDirection={animateStart}
                />
                <div className="col-1" />
              </>
            )}
            <div className="col-3">
              <h2 className="section-title">
                {getTextByKey("business-page-section-8-title")}
              </h2>
              <div className="content-text darker">
                <p>
                  {getHtmlByKey("business-page-section-8-info")}
                  <ButtonA
                    transparent
                    target="_blank"
                    page="jumbo-pay"
                    img={null}
                    title={getTextByKey("learn-more")}
                    buttonClass=""
                  >
                    {getTextByKey("learn-more")}
                  </ButtonA>
                </p>
              </div>
            </div>
            {!mobileCondition ? (
              <>
                <div className="col-1" />
                <ImageAnimatedWithContainer
                  containerClassName="col-3"
                  imagesNames={["business/phone"]}
                  ext="png"
                  className="graphic-full-width"
                  animationDirection={animateEnd}
                />
                <div className="col-1" />
              </>
            ) : (
              <div className="col-4">
                <Image
                  name={`business/jpay${mobileCondition ? "-mob" : ""}`}
                  ext="png"
                  className="graphic-full-width "
                />
              </div>
            )}
          </div>
        </>
      ) : null}
      <div className="content-section">
        <h2 className="section-title button-section">
          {getTextByKey("call-to-action-splitter-3")}
        </h2>
        <ButtonsContainer />
      </div>
    </div>
  );
};

export default Business;
